import * as d3 from "d3";
import { useCallback, useEffect, useRef, useState } from "react";
import { formatPercentage } from "../utils/numberUtils";
import { RETRO_CHART_MARGINS, RETRO_LINE_STYLES, type PerformanceChartVisualStyle } from "../components/performanceChartAppearance";

export interface ChartDataPoint {
  date: string;
  fullDate?: string;
  performance?: number | null;
  benchmark?: number | null;
  [key: string]: any;
}

export interface LineConfig {
  dataKey: string;
  stroke: string;
  strokeWidth: number;
  label: string;
  strokeDasharray?: string;
  showDots?: boolean;
  swatchClassName?: string;
}

export interface ReferenceLineConfig {
  x: string;
  stroke: string;
  strokeWidth: number;
  strokeDasharray: string;
  label: string;
}

export type ChartAnnotationTone = "positive" | "negative" | "neutral";
export type ChartAnnotationLabelPosition = "above" | "below";

export interface ChartAnnotationRangeConfig {
  startX: string;
  endX: string;
  className?: string;
}

export interface ChartAnnotationPointConfig {
  x: string;
  y: number;
  label: string;
  tone?: ChartAnnotationTone;
  radius?: number;
  labelPosition?: ChartAnnotationLabelPosition;
}

export interface XAxisTickConfig {
  value: string;
  label: string;
}

export interface D3LineChartConfig {
  data: ChartDataPoint[];
  lines: LineConfig[];
  isDarkMode: boolean;
  height?: number;
  /** Explicit plot height for hosts that fit the retro chart to their viewport. */
  retroPlotHeight?: number;
  margins?: { top: number; right: number; bottom: number; left: number };
  chartChrome?: "full" | "minimal";
  visualStyle?: PerformanceChartVisualStyle;
  yAxisFormatter?: (value: number) => string;
  yAxisMin?: number;
  tooltipFormatter?: (value: number, dataKey: string) => string;
  tooltipDateFormatter?: (date: Date) => string;
  // Break the line between recorded points farther apart than this interval.
  maxGapMs?: number;
  referenceLine?: ReferenceLineConfig;
  // Optional subtle marker (e.g., inception point)
  referencePoint?: { x: string; color?: string; radius?: number };
  annotationRanges?: ChartAnnotationRangeConfig[];
  annotationPoints?: ChartAnnotationPointConfig[];
  showLegend?: boolean;
  showEndLabels?: boolean;
  renderingMode?: "legacy" | "enhanced";
  xAxisTicks?: XAxisTickConfig[];
  // Render tooltip date labels and X-axis tick labels in UTC instead of the
  // browser's local timezone. Opt in for charts whose data points represent
  // calendar days stamped at UTC midnight (e.g. the strategy-vs-actual
  // comparison chart and the actual-only performance chart) so the labels
  // match the calendar day the data was reported for, even for users west of
  // UTC. Leave undefined/false for charts driven by intraday factors that
  // should render in the viewer's local time.
  useUtcDateLabels?: boolean;
}

const DEFAULT_ANNOTATION_RANGE_CLASS_NAME = "text-red-500/10";
const ANNOTATION_POINT_STYLES: Record<
  ChartAnnotationTone,
  { haloClassName: string; markerClassName: string; labelClassName: string }
> = {
  positive: {
    haloClassName: "text-green-500/20",
    markerClassName: "text-green-500",
    labelClassName: "fill-current text-[11px] font-semibold text-green-600 dark:text-green-400",
  },
  negative: {
    haloClassName: "text-red-500/20",
    markerClassName: "text-red-500",
    labelClassName: "fill-current text-[11px] font-semibold text-red-600 dark:text-red-400",
  },
  neutral: {
    haloClassName: "text-gray-500/20",
    markerClassName: "text-gray-500",
    labelClassName: "fill-current text-[11px] font-semibold text-gray-600 dark:text-gray-400",
  },
};

export function getChartAnnotationPointStyle(tone: ChartAnnotationTone = "neutral") {
  return ANNOTATION_POINT_STYLES[tone];
}

const HTML_ESCAPE_MAP: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  "\"": "&quot;",
  "'": "&#39;",
};

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => HTML_ESCAPE_MAP[char]);
}

export function getNextTrackedContainerWidth(
  prevWidth: number,
  measuredWidth: number | null | undefined
): number {
  if (typeof measuredWidth !== "number" || measuredWidth <= 0) {
    return prevWidth;
  }
  return prevWidth === measuredWidth ? prevWidth : measuredWidth;
}

export function resolveEffectiveContainerWidth(
  isEnhancedMode: boolean,
  trackedWidth: number,
  liveWidth: number
): number {
  if (!isEnhancedMode) return liveWidth;
  return trackedWidth > 0 ? trackedWidth : liveWidth;
}

export function parseChartDateString(value: string, isEnhancedMode: boolean): Date {
  if (!value) return new Date();
  if (isEnhancedMode && value.includes("T")) {
    const fullDate = new Date(value);
    if (!Number.isNaN(fullDate.getTime())) {
      return fullDate;
    }
  }
  const datePart = value.includes("T") ? value.split("T")[0] : value;
  const [yyyy, mm, dd] = datePart.split("-").map(Number);
  if (yyyy && mm && dd) {
    return new Date(yyyy, mm - 1, dd);
  }
  return new Date(value);
}

export function parseChartPointDate(point: ChartDataPoint, isEnhancedMode: boolean): Date {
  const value = isEnhancedMode ? point.fullDate ?? point.date : point.date;
  return parseChartDateString(value, isEnhancedMode);
}

export interface NearestLinePoint {
  dataPoint: ChartDataPoint;
  value: number;
  pointTimeMs: number;
  distanceMs: number;
}

export interface VisualNearestLinePoint extends NearestLinePoint {
  pointXPx: number;
  pointYPx: number;
  visualDistancePx: number;
  xDistancePx: number;
  yDistancePx: number;
}

export function getNearestLinePoint(
  lineData: ChartDataPoint[],
  dataKey: string,
  targetTimeMs: number,
  isEnhancedMode: boolean
): NearestLinePoint | null {
  if (lineData.length === 0) return null;

  let low = 0;
  let high = lineData.length;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    const midTime = parseChartPointDate(lineData[mid], isEnhancedMode).getTime();
    if (midTime < targetTimeMs) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  const candidates = [low - 1, low].filter(
    (index) => index >= 0 && index < lineData.length
  );
  let nearest: NearestLinePoint | null = null;

  candidates.forEach((index) => {
    const dataPoint = lineData[index];
    const value = dataPoint[dataKey];
    if (typeof value !== "number") return;
    const pointTimeMs = parseChartPointDate(dataPoint, isEnhancedMode).getTime();
    const distanceMs = Math.abs(pointTimeMs - targetTimeMs);
    if (!nearest || distanceMs < nearest.distanceMs) {
      nearest = { dataPoint, value, pointTimeMs, distanceMs };
    }
  });

  return nearest;
}

export function getNearestVisualLinePoint<T extends NearestLinePoint>(
  points: T[],
  mouseXPx: number,
  mouseYPx: number,
  xForTimestamp: (timestampMs: number) => number,
  yForValue: (value: number) => number
): (T & VisualNearestLinePoint) | null {
  let nearest: (T & VisualNearestLinePoint) | null = null;

  points.forEach((point) => {
    const pointXPx = xForTimestamp(point.pointTimeMs);
    const pointYPx = yForValue(point.value);
    if (!Number.isFinite(pointXPx) || !Number.isFinite(pointYPx)) return;

    const xDistancePx = Math.abs(pointXPx - mouseXPx);
    const yDistancePx = Math.abs(pointYPx - mouseYPx);
    const visualDistancePx = Math.hypot(xDistancePx, yDistancePx);
    const visualPoint = {
      ...point,
      pointXPx,
      pointYPx,
      visualDistancePx,
      xDistancePx,
      yDistancePx,
    };

    if (
      !nearest ||
      visualDistancePx < nearest.visualDistancePx ||
      (visualDistancePx === nearest.visualDistancePx && point.distanceMs < nearest.distanceMs)
    ) {
      nearest = visualPoint;
    }
  });

  return nearest;
}

export function filterLinePointsAtTimestamp<T extends NearestLinePoint>(
  points: T[],
  timestampMs: number
): T[] {
  return points.filter((point) => point.pointTimeMs === timestampMs);
}

export function formatXAxisPrimaryLabel(
  date: Date,
  hoursDiff: number,
  useUtc: boolean = false
): string {
  const formatHour = useUtc ? d3.utcFormat("%H:%M") : d3.timeFormat("%H:%M");
  const formatDayOfMonth = useUtc ? d3.utcFormat("%d") : d3.timeFormat("%d");
  return hoursDiff <= 72 ? formatHour(date) : formatDayOfMonth(date);
}

export function formatXAxisSecondaryLabel(
  date: Date,
  hoursDiff: number,
  useUtc: boolean = false
): string {
  const formatDate = useUtc ? d3.utcFormat("%b %d") : d3.timeFormat("%b %d");
  const formatMonth = useUtc ? d3.utcFormat("%b") : d3.timeFormat("%b");
  return hoursDiff <= 72 ? formatDate(date) : formatMonth(date);
}

export interface IntradaySecondaryTick {
  tick: Date;
  labelDate: Date;
}

export interface YearAxisTick {
  tick: Date;
  labelYear: number;
}

export function getIntradayMidnightBoundaries(domainStart: Date, domainEnd: Date, useUtc = false): Date[] {
  if (domainEnd <= domainStart) return [];

  const dayInterval = useUtc ? d3.utcDay : d3.timeDay;
  return dayInterval.range(dayInterval.ceil(domainStart), domainEnd)
    .filter(boundary => boundary > domainStart && boundary < domainEnd);
}

export function buildIntradaySecondaryTicks(domainStart: Date, domainEnd: Date, useUtc = false): IntradaySecondaryTick[] {
  if (domainEnd <= domainStart) return [];

  const boundaries = [
    new Date(domainStart),
    ...getIntradayMidnightBoundaries(domainStart, domainEnd, useUtc),
    new Date(domainEnd),
  ];

  const ticks: IntradaySecondaryTick[] = [];
  for (let index = 0; index < boundaries.length - 1; index++) {
    const segmentStart = boundaries[index];
    const segmentEnd = boundaries[index + 1];
    if (segmentEnd <= segmentStart) continue;

    ticks.push({
      tick: new Date((segmentStart.getTime() + segmentEnd.getTime()) / 2),
      labelDate: new Date(segmentStart),
    });
  }

  return ticks;
}

export function getYearTransitionBoundaries(
  domainStart: Date,
  domainEnd: Date,
  useUtc: boolean = false
): Date[] {
  if (domainEnd <= domainStart) return [];

  const boundaries: Date[] = [];
  const firstYear = useUtc ? domainStart.getUTCFullYear() : domainStart.getFullYear();
  for (let year = firstYear + 1; ; year++) {
    const boundary = useUtc
      ? new Date(Date.UTC(year, 0, 1))
      : new Date(year, 0, 1);
    if (boundary >= domainEnd) break;
    if (boundary > domainStart) boundaries.push(boundary);
  }

  return boundaries;
}

export function buildYearAxisTicks(
  domainStart: Date,
  domainEnd: Date,
  useUtc: boolean = false
): YearAxisTick[] {
  if (domainEnd <= domainStart) return [];

  const boundaries = [
    new Date(domainStart),
    ...getYearTransitionBoundaries(domainStart, domainEnd, useUtc),
    new Date(domainEnd),
  ];

  const ticks: YearAxisTick[] = [];
  for (let index = 0; index < boundaries.length - 1; index++) {
    const segmentStart = boundaries[index];
    const segmentEnd = boundaries[index + 1];
    if (segmentEnd <= segmentStart) continue;

    ticks.push({
      tick: new Date((segmentStart.getTime() + segmentEnd.getTime()) / 2),
      labelYear: useUtc ? segmentStart.getUTCFullYear() : segmentStart.getFullYear(),
    });
  }

  return ticks;
}

export const PRIMARY_X_AXIS_FONT_WEIGHT = "400";
export const SECONDARY_X_AXIS_FONT_WEIGHT = "500";
export const YEAR_X_AXIS_FONT_WEIGHT = "600";

function fitAxisLabelCenter(position: number, labelWidth: number, plotWidth: number): number {
  const inset = labelWidth / 2 + 3;
  return Math.max(inset, Math.min(plotWidth - inset, position));
}

export function dedupeTicksByFormattedLabel(
  ticks: number[],
  formatter: (value: number) => string
): number[] {
  const seen = new Set<string>();
  return ticks.filter((tick) => {
    const label = formatter(tick);
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });
}

export function resolveYTicksForMode(
  rawYTicks: number[],
  isEnhancedMode: boolean,
  formatter: (value: number) => string
): number[] {
  return isEnhancedMode ? dedupeTicksByFormattedLabel(rawYTicks, formatter) : rawYTicks;
}

export const useD3LineChart = (config: D3LineChartConfig) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const renderingMode = config.renderingMode ?? "legacy";
  const isEnhancedMode = renderingMode === "enhanced";

  const updateContainerWidth = useCallback(() => {
    const measuredWidth = containerRef.current?.offsetWidth;
    setContainerWidth(prevWidth => getNextTrackedContainerWidth(prevWidth, measuredWidth));
  }, []);

  useEffect(() => {
    if (!isEnhancedMode) return;
    updateContainerWidth();
  }, [isEnhancedMode, updateContainerWidth, config.data.length, config.height]);

  useEffect(() => {
    if (!isEnhancedMode) return;

    const containerNode = containerRef.current;
    if (!containerNode) return;

    let animationFrameId: number | null = null;
    const scheduleWidthUpdate = () => {
      if (animationFrameId !== null) return;
      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        updateContainerWidth();
      });
    };

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        scheduleWidthUpdate();
      });
      resizeObserver.observe(containerNode);
    }

    window.addEventListener("resize", scheduleWidthUpdate);

    return () => {
      window.removeEventListener("resize", scheduleWidthUpdate);
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      resizeObserver?.disconnect();
    };
  }, [isEnhancedMode, updateContainerWidth, config.data.length]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || config.data.length === 0) return;

    const {
      data,
      lines: inputLines,
      isDarkMode: inputDarkMode,
      height: inputHeight = 300,
      margins: inputMargins = { top: 16, right: 80, bottom: 48, left: 20 },
      yAxisFormatter = (value: number) => `${value}%`,
      yAxisMin,
      tooltipFormatter = (value: number, _dataKey: string) => formatPercentage(value),
      tooltipDateFormatter,
      maxGapMs,
      referenceLine,
      referencePoint,
      annotationRanges = [],
      annotationPoints = [],
      showLegend = true,
      showEndLabels = true,
      xAxisTicks,
      useUtcDateLabels = false,
      chartChrome = "full",
    } = config;
    const isRetro = config.visualStyle === "retro";
    const isDarkMode = isRetro ? false : inputDarkMode;
    const lines = isRetro
      ? inputLines.map(line => ({ ...line, ...(RETRO_LINE_STYLES[line.dataKey] ?? RETRO_LINE_STYLES.performance) }))
      : inputLines;
    const margins = isRetro ? RETRO_CHART_MARGINS : inputMargins;
    const showChartChrome = chartChrome === "full";

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    // Get container dimensions
    const liveContainerWidth = containerRef.current.offsetWidth;
    const actualContainerWidth = resolveEffectiveContainerWidth(
      isEnhancedMode,
      containerWidth,
      liveContainerWidth
    );
    const width = actualContainerWidth - margins.left - margins.right;
    if (width <= 0) return;
    const height = isRetro
      ? config.retroPlotHeight === undefined ? actualContainerWidth * 9 / 16 : config.retroPlotHeight + margins.top + margins.bottom
      : inputHeight;

    // Create SVG
    const svg = d3
      .select(svgRef.current)
      .attr("width", actualContainerWidth)
      .attr("height", height)
      .attr("viewBox", `0 0 ${actualContainerWidth} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet");

    const g = svg.append("g").attr("transform", `translate(${margins.left},${margins.top})`);

    // Define color scheme
    const gridColor = isRetro ? "var(--retro-grid)" : isDarkMode ? "#374151" : "#E5E7EB";
    const axisColor = isRetro ? "var(--retro-frame)" : isDarkMode ? "#9CA3AF" : "#6B7280";
    const textColor = isRetro ? "var(--retro-ink)" : isDarkMode ? "#D1D5DB" : "#6B7280";

    const parseDateString = (value: string) => parseChartDateString(value, isEnhancedMode);
    const parsePointDate = (point: ChartDataPoint) => parseChartPointDate(point, isEnhancedMode);

    const parsedDates = data.map(parsePointDate);
    const domainExtent = d3.extent(parsedDates) as [Date | undefined, Date | undefined];
    const domainStart = domainExtent[0];
    const domainEnd = domainExtent[1];

    if (!domainStart || !domainEnd) {
      return;
    }

    const timeSpanMs = Math.max(0, domainEnd.getTime() - domainStart.getTime());
    const hoursDiff = Math.ceil(timeSpanMs / (1000 * 60 * 60));
    const daysDiff = Math.ceil(timeSpanMs / (1000 * 60 * 60 * 24));
    const isNarrow = width <= 400;
    const showYearAxis = true;
    const axisLayerGap = isRetro ? 12 : isNarrow ? 16 : 18;
    const yearAxisLabelHeight = showYearAxis ? (isNarrow ? 14 : 15) : 0;
    const extraAxisHeight = showChartChrome
      ? isEnhancedMode
        ? axisLayerGap * 2 + yearAxisLabelHeight
        : showYearAxis
          ? width <= 400
            ? 16
            : 18
          : 0
      : 0;
    const chartHeight = height - margins.top - margins.bottom - (isRetro ? 0 : extraAxisHeight);

    // Create scales
    const xScale = d3.scaleTime().domain([domainStart, domainEnd]).range([0, width]);

    // Get all values for y-scale domain
    const allValues: number[] = [];
    lines.forEach(line => {
      data.forEach(d => {
        const value = d[line.dataKey];
        if (typeof value === "number") {
          allValues.push(value);
        }
      });
    });

    const yMin = Math.min(...allValues);
    const yMax = Math.max(...allValues);

    // Handle flat datasets: when all values are the same, expand domain by epsilon
    // to prevent NaN from identical domain endpoints
    let yPadding = (yMax - yMin) * 0.1;
    if (yMax === yMin) {
      // Use a small percentage of the value, or a minimum of 1 if value is 0
      yPadding = Math.abs(yMax) * 0.1 || 1;
    }

    const yScale = d3
      .scaleLinear()
      .domain([yAxisMin ?? yMin - yPadding, yMax + yPadding])
      .range([chartHeight, 0])
      .nice();

    // Compute Y ticks -- deduplicate by formatted label in enhanced mode only
    const rawYTicks = yScale.ticks(5);
    const yTicks = resolveYTicksForMode(rawYTicks, isEnhancedMode, yAxisFormatter);

    if (showChartChrome && isRetro) {
      const grid = g.append("g").attr("class", "retro-grid").attr("shape-rendering", "crispEdges");
      const subdivisions = isNarrow ? 16 : 32;
      for (let i = 1; i < subdivisions; i++) {
        const x = width * i / subdivisions;
        const y = chartHeight * i / subdivisions;
        const major = i % 4 === 0;
        for (const vertical of [true, false]) {
          grid.append("line")
            .attr("x1", vertical ? x : 0).attr("x2", vertical ? x : width)
            .attr("y1", vertical ? 0 : y).attr("y2", vertical ? chartHeight : y)
            .attr("stroke", major ? gridColor : "var(--retro-subgrid)")
            .attr("stroke-width", major ? 0.75 : 0.5);
        }
      }
      g.append("rect").attr("class", "retro-plot-frame")
        .attr("width", width).attr("height", chartHeight)
        .attr("fill", "none").attr("stroke", axisColor).attr("stroke-width", 0.8);
    } else if (showChartChrome) {
      // Add grid
      g.append("g")
        .attr("class", "grid")
        .selectAll("line.horizontal-grid")
        .data(yTicks)
        .enter()
        .append("line")
        .attr("class", "horizontal-grid")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", d => yScale(d))
        .attr("y2", d => yScale(d))
        .attr("stroke", gridColor)
        .attr("stroke-dasharray", "3 3")
        .attr("opacity", 0.5);

      // Add year boundary lines to visually separate years
      const yearInterval = useUtcDateLabels ? d3.utcYear : d3.timeYear;
      const yearBoundaries = yearInterval
        .range(yearInterval.floor(domainStart), yearInterval.ceil(domainEnd))
        .filter(date => date >= domainStart && date <= domainEnd);

      g.append("g")
        .attr("class", "year-boundaries")
        .selectAll("line.year-boundary")
        .data(yearBoundaries)
        .enter()
        .append("line")
        .attr("class", "year-boundary")
        .attr("x1", d => xScale(d))
        .attr("x2", d => xScale(d))
        .attr("y1", 0)
        .attr("y2", chartHeight)
        .attr("stroke", gridColor)
        .attr("stroke-dasharray", "2 3")
        .attr("opacity", 0.5);
    }

    // Emphasize the 0% baseline with a thin black line
    const zeroY = yScale(0);
    if (!Number.isNaN(zeroY)) {
      g.append("line")
        .attr("class", "zero-baseline")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", zeroY)
        .attr("y2", zeroY)
        .attr("stroke", "#000000")
        .attr("stroke-width", 0.5)
        .attr("opacity", 0.25);
    }

    if (annotationRanges.length > 0) {
      const rangeLayer = g
        .append("g")
        .attr("class", "chart-annotation-ranges")
        .style("pointer-events", "none");

      annotationRanges.forEach((range) => {
        const startDate = parseDateString(range.startX);
        const endDate = parseDateString(range.endX);
        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return;

        const startX = xScale(startDate);
        const endX = xScale(endDate);
        if (!Number.isFinite(startX) || !Number.isFinite(endX)) return;

        const rawLeft = Math.min(startX, endX);
        const rawRight = Math.max(startX, endX);
        if (rawRight < 0 || rawLeft > width) return;

        const left = Math.max(0, rawLeft);
        const right = Math.min(width, rawRight);
        const rangeWidth = Math.max(1, right - left);

        rangeLayer
          .append("rect")
          .attr("x", left)
          .attr("y", 0)
          .attr("width", rangeWidth)
          .attr("height", chartHeight)
          .attr("fill", "currentColor")
          .attr("class", range.className ?? DEFAULT_ANNOTATION_RANGE_CLASS_NAME);
      });
    }

    if (showChartChrome && isEnhancedMode) {
      const isIntraday = hoursDiff <= 72;
      const intradayMidnightBoundaries = isIntraday
        ? getIntradayMidnightBoundaries(domainStart, domainEnd, useUtcDateLabels)
        : [];

      if (intradayMidnightBoundaries.length > 0) {
        const transitionMarkerStartY = chartHeight + axisLayerGap + (isNarrow ? 1 : 2);
        const transitionMarkerEndY = transitionMarkerStartY + (isNarrow ? 6 : 7);
        const transitionMarkerColor = lines[0]?.stroke || axisColor;

        g.append("g")
          .attr("class", "day-transition-markers")
          .selectAll("line.day-transition-marker")
          .data(intradayMidnightBoundaries)
          .enter()
          .append("line")
          .attr("class", "day-transition-marker")
          .attr("x1", d => xScale(d))
          .attr("x2", d => xScale(d))
          .attr("y1", transitionMarkerStartY)
          .attr("y2", transitionMarkerEndY)
          .attr("stroke", transitionMarkerColor)
          .attr("stroke-width", 1)
          .attr("stroke-dasharray", "0")
          .attr("opacity", 0.95);
      }

      // Add 3-layer X axis:
      // Layer 1 (top): time for intraday, day-of-month for longer ranges
      // Layer 2 (middle): date for intraday, month for longer ranges
      // Layer 3 (bottom): year
      const isSameMoment = (a: Date, b: Date) => a.getTime() === b.getTime();

      const dedupeByInstant = (dates: Date[]): Date[] =>
        dates
          .sort((a, b) => a.getTime() - b.getTime())
          .filter((date, index, arr) => index === 0 || !isSameMoment(date, arr[index - 1]));

      const includeDomainBoundaries = (dates: Date[]): Date[] => {
        const enriched = [...dates, new Date(domainStart), new Date(domainEnd)];
        return dedupeByInstant(enriched);
      };

      const dedupeAdjacentByLabel = (
        dates: Date[],
        formatter: (d: Date) => string,
        duplicatePreference: "first" | "last" = "first"
      ): Date[] => {
        return dates.reduce((acc: Date[], date) => {
          if (acc.length === 0) {
            acc.push(date);
            return acc;
          }
          const prev = acc[acc.length - 1];
          if (formatter(prev) !== formatter(date)) {
            acc.push(date);
          } else if (duplicatePreference === "last") {
            acc[acc.length - 1] = date;
          }
          return acc;
        }, []);
      };

      const applyMinSpacing = (dates: Date[], minSpacingPx: number): Date[] => {
        const sorted = dedupeByInstant(dates);
        if (sorted.length <= 2) return sorted;

        const kept: Date[] = [sorted[0]];
        let lastAcceptedX = xScale(sorted[0]);

        for (let i = 1; i < sorted.length - 1; i++) {
          const currentX = xScale(sorted[i]);
          if (currentX - lastAcceptedX >= minSpacingPx) {
            kept.push(sorted[i]);
            lastAcceptedX = currentX;
          }
        }

        const last = sorted[sorted.length - 1];
        const lastX = xScale(last);
        if (lastX - lastAcceptedX < minSpacingPx && kept.length > 1) {
          kept.pop();
        }
        kept.push(last);

        return dedupeByInstant(kept);
      };

      const explicitXAxisTicks = (xAxisTicks ?? [])
        .map((tick) => ({
          date: parseDateString(tick.value),
          label: tick.label,
        }))
        .filter((tick) => !Number.isNaN(tick.date.getTime()))
        .sort((a, b) => a.date.getTime() - b.date.getTime());

      const renderAxisLayer = (
        tickValues: Date[],
        yOffset: number,
        formatter: (d: Date) => string,
        fontSize: string,
        fontWeight: string,
        alignEdgeTicks: boolean,
        showDomainLine: boolean
      ) => {
        const axis = g
          .append("g")
          .attr("transform", `translate(0,${chartHeight + yOffset})`)
          .call(
            d3
              .axisBottom(xScale)
              .tickValues(tickValues)
              .tickSize(0)
              .tickPadding(isNarrow ? 1 : 2)
              .tickFormat(d => formatter(d as Date))
          )
          .style("color", axisColor)
          .style("font-size", fontSize);

        if (showDomainLine) {
          axis.select(".domain").attr("stroke", gridColor);
        } else {
          axis.select(".domain").remove();
        }

        const texts = axis.selectAll<SVGTextElement, string>("text");
        texts
          .attr("fill", textColor)
          .style("font-weight", fontWeight)
          .style("text-anchor", "middle")
          .attr("dx", "0")
          .attr("dy", "0.8em");

        const nodes = texts.nodes();
        if (nodes.length === 1) {
          const tickX = tickValues.length === 1 ? xScale(tickValues[0]) : width / 2;
          if (tickX <= 1) {
            d3.select(nodes[0]).style("text-anchor", "start").attr("dx", "0.4em");
          } else if (tickX >= width - 1) {
            d3.select(nodes[0]).style("text-anchor", "end").attr("dx", "-0.4em");
          } else {
            d3.select(nodes[0]).style("text-anchor", "middle").attr("dx", "0");
          }
        } else if (alignEdgeTicks && nodes.length > 1) {
          d3.select(nodes[0]).style("text-anchor", "start").attr("dx", "0.4em");
          d3.select(nodes[nodes.length - 1]).style("text-anchor", "end").attr("dx", "-0.4em");
        }
        return axis;
      };

      if (explicitXAxisTicks.length > 0) {
        const labelsByTime = new Map<number, string>(
          explicitXAxisTicks.map((tick) => [tick.date.getTime(), tick.label])
        );
        const explicitTickValues = applyMinSpacing(
          explicitXAxisTicks.map((tick) => tick.date),
          isNarrow ? 58 : 72
        );

        renderAxisLayer(
          explicitTickValues,
          0,
          (date) => labelsByTime.get(date.getTime()) ?? formatXAxisPrimaryLabel(date, hoursDiff, useUtcDateLabels),
          isNarrow ? "9px" : "10px",
          PRIMARY_X_AXIS_FONT_WEIGHT,
          true,
          true
        );
      } else {
      const layer1MinSpacing = isIntraday ? (isNarrow ? 56 : 70) : isNarrow ? 34 : 42;
      const layer2MinSpacing = isNarrow ? 82 : 108;
      const layer1MaxTicks = Math.max(3, Math.floor(width / layer1MinSpacing));
      const layer2MaxTicks = Math.max(2, Math.floor(width / layer2MinSpacing));

      let layer1Ticks: Date[] = [];
      if (isIntraday) {
        const hourStep = Math.max(1, Math.ceil(hoursDiff / layer1MaxTicks));
        const hourInterval = d3.timeHour.every(hourStep);
        layer1Ticks = hourInterval ? xScale.ticks(hourInterval) : xScale.ticks(layer1MaxTicks);
      } else if (daysDiff <= 60) {
        const dayStep = Math.max(1, Math.ceil(daysDiff / layer1MaxTicks));
        const dayInterval = d3.timeDay.every(dayStep);
        layer1Ticks = dayInterval ? xScale.ticks(dayInterval) : xScale.ticks(layer1MaxTicks);
      } else {
        const weekStep = Math.max(1, Math.ceil(daysDiff / (7 * layer1MaxTicks)));
        const weekInterval = d3.timeWeek.every(weekStep);
        layer1Ticks = weekInterval ? xScale.ticks(weekInterval) : xScale.ticks(layer1MaxTicks);
      }
      layer1Ticks = includeDomainBoundaries(layer1Ticks);
      layer1Ticks = applyMinSpacing(layer1Ticks, layer1MinSpacing);
      layer1Ticks = dedupeAdjacentByLabel(
        layer1Ticks,
        (d) => formatXAxisPrimaryLabel(d, hoursDiff, useUtcDateLabels),
        "last"
      );

      let layer2Ticks: Date[] = [];
      let layer2Formatter = (date: Date) => formatXAxisSecondaryLabel(date, hoursDiff, useUtcDateLabels);
      if (isIntraday) {
        const intradaySecondaryTicks = buildIntradaySecondaryTicks(domainStart, domainEnd, useUtcDateLabels);
        const intradaySecondaryLabels = new Map<number, string>(
          intradaySecondaryTicks.map(({ tick, labelDate }) => [
            tick.getTime(),
            formatXAxisSecondaryLabel(labelDate, hoursDiff, useUtcDateLabels),
          ])
        );

        layer2Ticks = intradaySecondaryTicks.map(({ tick }) => tick);
        layer2Formatter = (date: Date) =>
          intradaySecondaryLabels.get(date.getTime()) ?? formatXAxisSecondaryLabel(date, hoursDiff, useUtcDateLabels);

        layer2Ticks = applyMinSpacing(layer2Ticks, layer2MinSpacing);
        layer2Ticks = dedupeAdjacentByLabel(layer2Ticks, layer2Formatter);
      } else {
        const startMonth = new Date(domainStart.getFullYear(), domainStart.getMonth(), 1);
        const endMonth = new Date(domainEnd.getFullYear(), domainEnd.getMonth(), 1);
        const monthDiff =
          (endMonth.getFullYear() - startMonth.getFullYear()) * 12 +
          (endMonth.getMonth() - startMonth.getMonth()) +
          1;
        const monthStep = Math.max(1, Math.ceil(monthDiff / layer2MaxTicks));
        const monthInterval = d3.timeMonth.every(monthStep);
        layer2Ticks = monthInterval ? xScale.ticks(monthInterval) : xScale.ticks(layer2MaxTicks);

        layer2Ticks = includeDomainBoundaries(layer2Ticks);
        layer2Ticks = dedupeAdjacentByLabel(layer2Ticks, layer2Formatter);
        layer2Ticks = applyMinSpacing(layer2Ticks, layer2MinSpacing);
        layer2Ticks = dedupeAdjacentByLabel(layer2Ticks, layer2Formatter);
      }

      const primaryAxisFontSize = isNarrow ? "9px" : "10px";
      const secondaryAxisFontSize = isNarrow ? "10px" : "11px";
      const yearAxisFontSize = isNarrow ? "11px" : "12px";

      renderAxisLayer(
        layer1Ticks,
        0,
        (date) => formatXAxisPrimaryLabel(date, hoursDiff, useUtcDateLabels),
        primaryAxisFontSize,
        PRIMARY_X_AXIS_FONT_WEIGHT,
        true,
        true
      );
      renderAxisLayer(
        layer2Ticks,
        axisLayerGap,
        layer2Formatter,
        secondaryAxisFontSize,
        SECONDARY_X_AXIS_FONT_WEIGHT,
        !isIntraday,
        false
      );

      if (showYearAxis) {
        const yearAxisTicks = buildYearAxisTicks(
          domainStart,
          domainEnd,
          useUtcDateLabels
        );
        const yearLabelsByTick = new Map<number, string>(
          yearAxisTicks.map(({ tick, labelYear }) => [tick.getTime(), labelYear.toString()])
        );
        const yearLabelFormatter = (date: Date) =>
          yearLabelsByTick.get(date.getTime())
          ?? (useUtcDateLabels ? date.getUTCFullYear() : date.getFullYear()).toString();

        let visibleYearTicks = yearAxisTicks.map(({ tick }) => tick);
        visibleYearTicks = applyMinSpacing(visibleYearTicks, isNarrow ? 54 : 66);
        visibleYearTicks = dedupeAdjacentByLabel(visibleYearTicks, yearLabelFormatter);

        const yearAxis = renderAxisLayer(
          visibleYearTicks,
          axisLayerGap * 2,
          yearLabelFormatter,
          yearAxisFontSize,
          YEAR_X_AXIS_FONT_WEIGHT,
          false,
          false
        );

        // A partial year can occupy only a few pixels at either edge. Keep its
        // full label inside the plot without changing the underlying date tick.
        const yearLabelBounds: Array<{ left: number; right: number }> = [];
        yearAxis.selectAll<SVGTextElement, Date>("text").each(function (date) {
          const labelWidth = this.getBBox().width;
          const tickX = xScale(date);
          const center = fitAxisLabelCenter(tickX, labelWidth, width);
          d3.select(this).style("text-anchor", "middle").attr("dx", "0").attr("x", center - tickX);
          yearLabelBounds.push({ left: center - labelWidth / 2, right: center + labelWidth / 2 });
        });

        const yearTransitionBoundaries = getYearTransitionBoundaries(
          domainStart,
          domainEnd,
          useUtcDateLabels
        ).filter(boundary => !yearLabelBounds.some(bounds => {
          const markerX = xScale(boundary);
          return markerX >= bounds.left - 2 && markerX <= bounds.right + 2;
        }));
        if (yearTransitionBoundaries.length > 0) {
          const yearMarkerStartY = chartHeight + axisLayerGap * 2 + (isNarrow ? 1 : 2);
          const yearMarkerEndY = yearMarkerStartY + (isNarrow ? 6 : 7);
          const yearMarkerColor = lines[0]?.stroke || axisColor;

          g.append("g")
            .attr("class", "year-transition-markers")
            .selectAll("line.year-transition-marker")
            .data(yearTransitionBoundaries)
            .enter()
            .append("line")
            .attr("class", "year-transition-marker")
            .attr("x1", d => xScale(d))
            .attr("x2", d => xScale(d))
            .attr("y1", yearMarkerStartY)
            .attr("y2", yearMarkerEndY)
            .attr("stroke", yearMarkerColor)
            .attr("stroke-width", 1)
            .attr("stroke-dasharray", "0")
            .attr("opacity", 0.95);
        }
      }
      }
    } else if (showChartChrome) {
      // Add X axis with adaptive ticks based on available width and time span
      // Increased breakpoint to 400px to catch more mobile devices
      // Increased min spacing for narrow screens to 75px to prevent overlap
      const minLabelSpacingPx = Math.max(isNarrow ? 75 : 52, Math.min(96, Math.floor(width / 8)));
      const maxTicks = Math.max(3, Math.floor(width / minLabelSpacingPx));

      let tickInterval: d3.TimeInterval | null;
      let isMonthlyTicks = false;

      if (daysDiff <= 14) {
        const dayStep = Math.max(1, Math.ceil(daysDiff / maxTicks));
        tickInterval = d3.timeDay.every(dayStep);
      } else if (daysDiff <= 60) {
        const dayStep = Math.max(1, Math.ceil(daysDiff / maxTicks));
        tickInterval = d3.timeDay.every(dayStep);
      } else if (daysDiff <= 180) {
        const weekStep = Math.max(1, Math.ceil(daysDiff / (7 * maxTicks)));
        tickInterval = d3.timeWeek.every(weekStep);
      } else {
        const startMonth = new Date(domainStart.getFullYear(), domainStart.getMonth(), 1);
        const endMonth = new Date(domainEnd.getFullYear(), domainEnd.getMonth(), 1);
        const monthDiff =
          (endMonth.getFullYear() - startMonth.getFullYear()) * 12 +
          (endMonth.getMonth() - startMonth.getMonth()) +
          1;
        const monthStep = Math.max(1, Math.ceil(monthDiff / maxTicks));
        tickInterval = d3.timeMonth.every(monthStep);
        isMonthlyTicks = true;
      }
      const sameMonthYear = (a: Date, b: Date) =>
        a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
      const isSameDay = (a: Date, b: Date) => a.getTime() === b.getTime();

      let tickValues = tickInterval ? xScale.ticks(tickInterval) : xScale.ticks(maxTicks);
      const ensureContainsDate = (dates: Date[], target: Date) => {
        if (!dates.some(date => isSameDay(date, target))) {
          dates.push(new Date(target));
        }
      };
      ensureContainsDate(tickValues, domainStart);
      ensureContainsDate(tickValues, domainEnd);

      const prefersBoundary = (date: Date) => isSameDay(date, domainStart) || isSameDay(date, domainEnd);

      tickValues = tickValues
        .sort((a, b) => a.getTime() - b.getTime())
        .reduce((acc: Date[], date) => {
          if (acc.length === 0) {
            acc.push(date);
            return acc;
          }

          const prev = acc[acc.length - 1];
          if (isSameDay(prev, date)) {
            return acc;
          }

          if (isMonthlyTicks && sameMonthYear(prev, date)) {
            const prevPrefersBoundary = prefersBoundary(prev);
            const currentPrefersBoundary = prefersBoundary(date);

            if (prevPrefersBoundary && currentPrefersBoundary) {
              acc.push(date);
            } else if (currentPrefersBoundary && !prevPrefersBoundary) {
              acc[acc.length - 1] = date;
            } else if (!prevPrefersBoundary && !currentPrefersBoundary) {
              acc[acc.length - 1] = date;
            }
          } else {
            acc.push(date);
          }

          return acc;
        }, []);

      if (tickValues.length >= 2) {
        const filteredTickValues: Date[] = [];
        // Enforce a hard floor of 60px for spacing threshold to strictly prevent overlap
        const minSpacingThreshold = Math.max(60, minLabelSpacingPx * 0.85);
        let lastAcceptedX: number | null = null;

        tickValues.forEach((value, index) => {
          const xPosition = xScale(value);
          const isFirst = index === 0;
          const isLast = index === tickValues.length - 1;

          if (isFirst) {
            filteredTickValues.push(value);
            lastAcceptedX = xPosition;
            return;
          }

          if (isLast) {
            // Special handling for the last tick because it is text-anchor: end
            // This means it extends to the LEFT of its x-position.
            // Previous ticks are text-anchor: middle, extending RIGHT by half their width.
            // We need to ensure: x_last - width_last > x_prev + width_prev/2

            let requiredSpacing = minSpacingThreshold;

            // Check if we're likely to show the year on this last tick
            const prevAccepted = filteredTickValues[filteredTickValues.length - 1];
            const yearChanged = prevAccepted && prevAccepted.getFullYear() !== value.getFullYear();

            // If year changed (or standard long range), the label is wide (~80px for "Jan 13, 2026")
            // Previous label is ~40px ("Dec 07"). Half is 20px.
            // Required gap: 80 + 20 + padding = ~110px
            if (yearChanged || daysDiff >= 365) {
              requiredSpacing = 110;
            } else {
              // Standard label (~40px). Required gap: 40 + 20 + padding = ~70px
              requiredSpacing = 70;
            }

            if (
              lastAcceptedX !== null &&
              Math.abs(xPosition - lastAcceptedX) < requiredSpacing &&
              filteredTickValues.length
            ) {
              filteredTickValues.pop();
            }
            filteredTickValues.push(value);
            lastAcceptedX = xPosition;
            return;
          }

          if (lastAcceptedX === null || Math.abs(xPosition - lastAcceptedX) >= minSpacingThreshold) {
            filteredTickValues.push(value);
            lastAcceptedX = xPosition;
          }
        });

        tickValues = filteredTickValues;
      }

      const formatMonth = d3.timeFormat("%b");
      const formatDay = d3.timeFormat("%b %d");
      const tickFormatter = (date: Date) => {
        const useDayFormat = daysDiff <= 180;
        return useDayFormat ? formatDay(date) : formatMonth(date);
      };

      const xAxis = g
        .append("g")
        .attr("transform", `translate(0,${chartHeight})`)
        .call(
          d3
            .axisBottom(xScale)
            .tickValues(tickValues)
            .tickSize(0)
            .tickPadding(isNarrow ? 2 : 3)
            .tickFormat(d => tickFormatter(d as Date))
        )
        .style("color", axisColor)
        .style("font-size", isNarrow ? "10px" : "11px");

      xAxis.select(".domain").attr("stroke", gridColor);

      const xAxisTexts = xAxis.selectAll<SVGTextElement, string>("text");

      xAxisTexts.attr("fill", textColor).style("text-anchor", "middle").attr("dx", "0").attr("dy", "0.8em");

      const textNodes = xAxisTexts.nodes();
      const totalTicks = textNodes.length;
      if (totalTicks === 1) {
        d3.select(textNodes[0]).style("text-anchor", "middle").attr("dx", "0");
      } else if (totalTicks > 1) {
        d3.select(textNodes[0]).style("text-anchor", "start").attr("dx", "0.5em");
        d3.select(textNodes[totalTicks - 1]).style("text-anchor", "end").attr("dx", "-0.5em");
      }

      // Add a secondary year-only axis to preserve context without crowding day/month labels
      if (showYearAxis) {
        const startYear = domainStart.getFullYear();
        const endYear = domainEnd.getFullYear();
        const yearAxisOffset = extraAxisHeight; // Vertical offset for the year labels

        // Group for year labels
        const yearAxisGroup = g
          .append("g")
          .attr("class", "year-axis")
          .attr("transform", `translate(0,${chartHeight + yearAxisOffset})`);

        for (let y = startYear; y <= endYear; y++) {
          const yearStart = new Date(y, 0, 1);
          const yearEnd = new Date(y + 1, 0, 1);

          // Determine the visible range for this year
          const visibleStart = yearStart < domainStart ? domainStart : yearStart;
          const visibleEnd = yearEnd > domainEnd ? domainEnd : yearEnd;

          // Skip if for some reason the year isn't actually in range (shouldn't happen with our loop bounds)
          if (visibleStart >= visibleEnd) continue;

          const midTime = new Date((visibleStart.getTime() + visibleEnd.getTime()) / 2);
          const xPos = xScale(midTime);

          // Render the year label
          const yearLabel = yearAxisGroup
            .append("text")
            .attr("x", xPos)
            .attr("y", 9) // Approximate vertical alignment similar to standard axis ticks
            .attr("text-anchor", "middle")
            .attr("fill", isDarkMode ? "#FFFFFF" : "#000000")
            .style("font-size", "10px")
            .style("font-weight", "bold")
            .text(y.toString());
          yearLabel.attr("x", fitAxisLabelCenter(xPos, yearLabel.node()?.getBBox().width ?? 0, width));
        }
      }
    }

    if (showChartChrome) {
      // Add Y axis on the RIGHT side
      const yAxisRight = g
        .append("g")
        .attr("transform", `translate(${width},0)`)
        .call(
          isEnhancedMode
            ? d3
                .axisRight(yScale)
                .tickValues(yTicks)
                .tickFormat(d => yAxisFormatter(d as number))
            : d3
                .axisRight(yScale)
                .ticks(5)
                .tickFormat(d => yAxisFormatter(d as number))
        )
        .style("color", axisColor)
        .style("font-size", isRetro ? "10px" : "12px");

      yAxisRight.select(".domain").attr("stroke", gridColor);
      yAxisRight.selectAll("text").attr("fill", textColor);
      yAxisRight.selectAll(".tick line").attr("stroke", gridColor);
      if (isRetro) {
        yAxisRight.selectAll<SVGTextElement, unknown>("text").each(function () {
          const availableWidth = margins.right - Number(this.getAttribute("x")) - 4;
          if (this.getBBox().width > availableWidth) {
            d3.select(this).attr("textLength", availableWidth).attr("lengthAdjust", "spacingAndGlyphs");
          }
        });
      }
    }

    // Create line generator
    const lineGenerator = d3
      .line<ChartDataPoint>()
      .x(d => xScale(parsePointDate(d)))
      .curve(d3.curveLinear)
      .defined(d => d !== null);

    // Add reference line if provided
    if (referenceLine) {
      const referenceDate = parseDateString(referenceLine.x);
      if (!Number.isNaN(referenceDate.getTime())) {
        const refX = xScale(referenceDate);

        g.append("line")
          .attr("x1", refX)
          .attr("x2", refX)
          .attr("y1", 0)
          .attr("y2", chartHeight)
          .attr("stroke", referenceLine.stroke)
          .attr("stroke-width", referenceLine.strokeWidth)
          .attr("stroke-dasharray", referenceLine.strokeDasharray);

        g.append("text")
          .attr("x", refX)
          .attr("y", -10)
          .attr("text-anchor", "middle")
          .attr("fill", referenceLine.stroke)
          .attr("font-size", "12px")
          .attr("font-weight", "600")
          .text(referenceLine.label);
      }
    }

    // Add subtle reference point marker if provided
    if (referencePoint && referencePoint.x) {
      const markerColor = referencePoint.color || (isDarkMode ? "#9CA3AF" : "#6B7280");
      const markerRadius = referencePoint.radius ?? 5;

      // Find nearest domain date to the provided x
      const target = parseDateString(referencePoint.x);
      const domainDates = data.map(parsePointDate);
      let bestIdx = 0;
      let bestDelta = Math.abs(domainDates[0].getTime() - target.getTime());
      for (let i = 1; i < domainDates.length; i++) {
        const delta = Math.abs(domainDates[i].getTime() - target.getTime());
        if (delta < bestDelta) {
          bestIdx = i;
          bestDelta = delta;
        }
      }
      const firstLineKey = lines[0]?.dataKey;
      if (firstLineKey) {
        const dp = data[bestIdx];
        const nearestDate = domainDates[bestIdx];
        const value = typeof dp[firstLineKey] === "number" ? (dp[firstLineKey] as number) : 0;
        const cx = xScale(nearestDate);
        const cy = yScale(value);
        if (!Number.isNaN(cy)) {
          // Core marker
          g.append("circle")
            .attr("cx", cx)
            .attr("cy", cy)
            .attr("r", markerRadius)
            .attr("fill", markerColor)
            .attr("stroke", isDarkMode ? "#111827" : "#FFFFFF")
            .attr("stroke-width", 1)
            .attr("opacity", 0.9);

          // Pulsing halo
          const halo = g
            .append("circle")
            .attr("cx", cx)
            .attr("cy", cy)
            .attr("r", markerRadius + 2)
            .attr("fill", markerColor)
            .attr("opacity", 0.35);

          const pulse = () => {
            halo
              .attr("r", markerRadius + 2)
              .attr("opacity", 0.35)
              .transition()
              .duration(1400)
              .ease(d3.easeCubicOut)
              .attr("r", markerRadius + 10)
              .attr("opacity", 0)
              .on("end", pulse);
          };
          pulse();
        }
      }
    }

    // Draw lines
    const lineDataByKey = new Map<string, ChartDataPoint[]>();
    lines.forEach(lineConfig => {
      const lineData = data.filter(d => d[lineConfig.dataKey] !== undefined && d[lineConfig.dataKey] !== null);
      lineDataByKey.set(lineConfig.dataKey, lineData);

      const segments: ChartDataPoint[][] = [];
      lineData.forEach((point, index) => {
        const previous = lineData[index - 1];
        if (!previous || (maxGapMs !== undefined
          && parsePointDate(point).getTime() - parsePointDate(previous).getTime() > maxGapMs)) {
          segments.push([]);
        }
        segments[segments.length - 1].push(point);
      });

      // Separate paths preserve gaps without inserting synthetic periods.
      segments.forEach((segment) => {
        const path = g
          .append("path")
          .datum(segment)
          .attr("fill", "none")
          .attr("stroke", lineConfig.stroke)
          .attr("stroke-width", lineConfig.strokeWidth)
          .attr(
            "d",
            lineGenerator.y(d => yScale(d[lineConfig.dataKey] as number))
          );

        if (lineConfig.strokeDasharray) {
          path.attr("stroke-dasharray", lineConfig.strokeDasharray);
        }
      });

      // Add dots if enabled
      if (lineConfig.showDots !== false && (lineData.length < 50 || maxGapMs !== undefined)) {
        g.selectAll(`.dot-${lineConfig.dataKey}`)
          .data(lineData)
          .enter()
          .append("circle")
          .attr("class", `dot-${lineConfig.dataKey}`)
          .attr("cx", d => xScale(parsePointDate(d)))
          .attr("cy", d => yScale(d[lineConfig.dataKey] as number))
          .attr("r", 4)
          .attr("fill", lineConfig.stroke)
          .attr("stroke", "white")
          .attr("stroke-width", 1.5);
      }

      // End-of-line value labels are rendered in a separate de-collided pass
      // below, once every line's endpoint is known.
    });

    // Right-edge value labels. Measure each, then spread their vertical centers
    // apart with a minimum gap so labels for lines that end close together
    // (e.g. gross vs net of fees) do not overlap. Centers stay inside the plot.
    const horizontalLabelPadding = 4;
    const verticalLabelPadding = 2;
    const endLabelEntries = (showChartChrome && showEndLabels && !isRetro ? lines : [])
      .map((lineConfig) => {
        const lineData = lineDataByKey.get(lineConfig.dataKey) ?? [];
        if (lineData.length === 0) return null;
        const lastPoint = lineData[lineData.length - 1];
        const lastValue = lastPoint[lineConfig.dataKey];
        if (typeof lastValue !== "number") return null;

        const lastX = xScale(parsePointDate(lastPoint));
        const desiredY = yScale(lastValue);
        const labelGroup = g.append("g").attr("class", "right-end-label").style("pointer-events", "none");
        const textEl = labelGroup
          .append("text")
          .attr("x", lastX + horizontalLabelPadding)
          .attr("dy", "0.35em")
          .attr("fill", lineConfig.stroke)
          .attr("font-size", "11px")
          .attr("font-weight", "700")
          .attr("text-anchor", "start")
          .text(tooltipFormatter(lastValue, lineConfig.dataKey));

        const maxWidth = width + margins.right - lastX - 2; // keep inside svg viewport
        let boxWidth = maxWidth;
        let boxHeight = 14;
        const textNode = textEl.node() as SVGTextElement | null;
        if (textNode) {
          let bbox = textNode.getBBox();
          // Shrink progressively if the value would overflow the right margin.
          if (bbox.width + horizontalLabelPadding * 2 > maxWidth) {
            textEl.attr("font-size", "10px");
            bbox = textNode.getBBox();
          }
          if (bbox.width + horizontalLabelPadding * 2 > maxWidth) {
            textEl.attr("font-size", "9px");
            bbox = textNode.getBBox();
          }
          boxWidth = Math.min(bbox.width + horizontalLabelPadding * 2, maxWidth);
          boxHeight = bbox.height + verticalLabelPadding;
        }

        return { stroke: lineConfig.stroke, lastX, desiredY, textEl, labelGroup, boxWidth, boxHeight };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);

    if (endLabelEntries.length > 0) {
      const maxBoxHeight = Math.max(...endLabelEntries.map((entry) => entry.boxHeight));
      const minGap = maxBoxHeight + 2;
      const minCenter = maxBoxHeight / 2;
      const maxCenter = Math.max(minCenter, chartHeight - maxBoxHeight / 2);

      // Resolve vertical positions in draw order, then commit by ascending Y.
      const order = endLabelEntries
        .map((_, index) => index)
        .sort((a, b) => endLabelEntries[a].desiredY - endLabelEntries[b].desiredY);

      const centers: number[] = [];
      order.forEach((entryIndex, k) => {
        const desired = endLabelEntries[entryIndex].desiredY;
        centers[k] = k === 0 ? desired : Math.max(desired, centers[k - 1] + minGap);
      });
      // Pull back inside the bottom edge, correcting upward if needed.
      if (centers[centers.length - 1] > maxCenter) {
        centers[centers.length - 1] = maxCenter;
        for (let k = centers.length - 2; k >= 0; k--) {
          centers[k] = Math.min(centers[k], centers[k + 1] - minGap);
        }
      }
      // Pull back inside the top edge, correcting downward if needed.
      if (centers[0] < minCenter) {
        centers[0] = minCenter;
        for (let k = 1; k < centers.length; k++) {
          centers[k] = Math.max(centers[k], centers[k - 1] + minGap);
        }
      }

      order.forEach((entryIndex, k) => {
        const entry = endLabelEntries[entryIndex];
        const centerY = centers[k];
        entry.textEl.attr("y", centerY);
        entry.labelGroup
          .insert("rect", "text")
          .attr("x", entry.lastX)
          .attr("y", centerY - entry.boxHeight / 2)
          .attr("width", entry.boxWidth)
          .attr("height", entry.boxHeight)
          .attr("rx", 4)
          .attr("ry", 4)
          .attr("fill", isDarkMode ? "#111827" : "#ffffff")
          .attr("stroke", entry.stroke)
          .attr("stroke-width", 1);
      });
    }

    if (isRetro && showChartChrome && showEndLabels) {
      const labelLayer = g.append("g").attr("class", "retro-point-labels").attr("pointer-events", "none");
      const containerRect = containerRef.current.getBoundingClientRect();
      const occupied = Array.from(containerRef.current.querySelectorAll("[data-chart-legend], [data-chart-watermark]"))
        .map(node => node.getBoundingClientRect())
        .filter(rect => rect.width > 0 && rect.height > 0)
        .map(rect => ({
          x: rect.left - containerRect.left - margins.left,
          y: rect.top - containerRect.top - margins.top,
          width: rect.width,
          height: rect.height,
        }));
      const series = lines.map(line => ({
        line,
        points: (lineDataByKey.get(line.dataKey) ?? []).map(point => ({
          x: xScale(parsePointDate(point)),
          y: yScale(point[line.dataKey]),
          value: point[line.dataKey] as number,
        })).filter(point => Number.isFinite(point.x) && Number.isFinite(point.y)),
      }));
      const samples = series.flatMap(item => item.points).sort((a, b) => a.x - b.x);
      const sampleIndex = d3.bisector((point: { x: number }) => point.x).left;

      series.forEach(({ line, points }) => {
        const candidates = points.flatMap((point, index) => {
          if (index === points.length - 1) return [{ point, peak: true, priority: Infinity }];
          if (index < 2 || index > points.length - 3) return [];
          const neighbors = [points[index - 2], points[index - 1], points[index + 1], points[index + 2]];
          const peak = neighbors.every(other => point.value > other.value);
          const dip = neighbors.every(other => point.value < other.value);
          if (!peak && !dip) return [];
          return [{ point, peak, priority: Math.min(...neighbors.map(other => Math.abs(point.y - other.y))) }];
        }).sort((a, b) => b.priority - a.priority).slice(0, isNarrow ? 12 : 28);
        let count = 0;
        for (const { point, peak } of candidates) {
          if (count >= (isNarrow ? 3 : 7)) break;
          const label = labelLayer.append("g").attr("class", "retro-point-label")
            .attr("data-series", line.dataKey).attr("data-value", point.value);
          const text = label.append("text").attr("x", 1.5).attr("y", 8.5)
            .attr("fill", textColor).attr("font-size", 9).attr("font-weight", 400)
            .text(tooltipFormatter(point.value, line.dataKey));
          const boxWidth = Math.ceil(text.node()!.getBBox().width) + 3;
          const boxHeight = 11;
          const x = Math.max(3, Math.min(width - boxWidth - 3, point.x - boxWidth / 2));
          const nearbySamples = samples.slice(sampleIndex(samples, x - 1), sampleIndex(samples, x + boxWidth + 1));
          // Prefer the outside of a turn; try the other side when lines meet.
          const positions = peak ? [point.y - boxHeight - 2, point.y + 2] : [point.y + 2, point.y - boxHeight - 2];
          const y = positions.find(top => top >= 3 && top + boxHeight <= chartHeight - 3
            && !occupied.some(other => x < other.x + other.width + 4 && x + boxWidth + 4 > other.x
              && top < other.y + other.height + 4 && top + boxHeight + 4 > other.y)
            && !nearbySamples.some(sample => sample.y >= top - 1 && sample.y <= top + boxHeight + 1));
          if (y === undefined || boxWidth > width - 6) {
            label.remove();
            continue;
          }
          label.attr("transform", `translate(${x},${y})`);
          label.insert("line", "text").attr("x1", point.x - x).attr("x2", point.x - x)
            .attr("y1", point.y - y).attr("y2", y < point.y ? boxHeight : 0)
            .attr("stroke", line.stroke).attr("stroke-width", 0.5);
          label.insert("rect", "text").attr("width", boxWidth).attr("height", boxHeight)
            .attr("fill", "var(--retro-paper)").attr("stroke", axisColor).attr("stroke-width", 0.5);
          occupied.push({ x, y, width: boxWidth, height: boxHeight });
          count++;
        }
      });
    }

    if (annotationPoints.length > 0) {
      const pointLayer = g
        .append("g")
        .attr("class", "chart-annotation-points")
        .style("pointer-events", "none");

      annotationPoints.forEach((point) => {
        const pointDate = parseDateString(point.x);
        if (Number.isNaN(pointDate.getTime()) || !Number.isFinite(point.y)) return;

        const cx = xScale(pointDate);
        const cy = yScale(point.y);
        if (!Number.isFinite(cx) || !Number.isFinite(cy)) return;

        const radius = point.radius ?? 4;
        const tone = point.tone ?? "neutral";
        const styles = getChartAnnotationPointStyle(tone);
        const labelPosition = point.labelPosition ?? (tone === "negative" ? "below" : "above");
        const labelY =
          labelPosition === "below"
            ? Math.min(chartHeight - 8, cy + 18)
            : Math.max(12, cy - 10);
        const labelEdgeBuffer = 36;
        let labelX = cx;
        let textAnchor: "start" | "middle" | "end" = "middle";

        if (cx < labelEdgeBuffer) {
          labelX = 4;
          textAnchor = "start";
        } else if (cx > width - labelEdgeBuffer) {
          labelX = width - 4;
          textAnchor = "end";
        }

        pointLayer
          .append("circle")
          .attr("cx", cx)
          .attr("cy", cy)
          .attr("r", radius + 5)
          .attr("fill", "currentColor")
          .attr("class", styles.haloClassName);

        pointLayer
          .append("circle")
          .attr("cx", cx)
          .attr("cy", cy)
          .attr("r", radius)
          .attr("fill", "currentColor")
          .attr("class", styles.markerClassName);

        pointLayer
          .append("text")
          .attr("x", labelX)
          .attr("y", labelY)
          .attr("text-anchor", textAnchor)
          .attr("class", styles.labelClassName)
          .text(point.label);
      });
    }

    // Add invisible overlay for hover interactions
    const overlay = g
      .append("rect")
      .attr("class", "overlay")
      .attr("width", width)
      .attr("height", chartHeight)
      .attr("fill", "none")
      .attr("pointer-events", "all");

    // Create focus elements for hover
    const focus = g.append("g").style("display", "none");

    focus
      .append("line")
      .attr("class", "hover-line")
      .attr("y1", 0)
      .attr("y2", chartHeight)
      .attr("stroke", isDarkMode ? "#6B7280" : "#9CA3AF")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "3 3");

    lines.forEach(lineConfig => {
      focus
        .append("circle")
        .attr("class", `focus-circle-${lineConfig.dataKey}`)
        .attr("r", 6)
        .attr("fill", lineConfig.stroke)
        .attr("stroke", "white")
        .attr("stroke-width", 2);
    });

    // Tooltip
    const tooltip = d3.select(tooltipRef.current);
    // Ensure tooltip positioning is relative to the chart container to avoid transform-based offset issues
    tooltip.style("position", "absolute");

    // Mouse move handler
    overlay
      .on("mouseover", () => {
        focus.style("display", null);
        tooltip.style("display", "block");
      })
      .on("mouseout", () => {
        focus.style("display", "none");
        tooltip.style("display", "none");
      })
      .on("mousemove", function (event) {
        const [mouseX, mouseY] = d3.pointer(event);
        const hoveredDate = xScale.invert(mouseX);
        const hoveredTimeMs = hoveredDate.getTime();
        const hoveredLinePoints = lines
          .map((lineConfig) => {
            const nearest = getNearestLinePoint(
              lineDataByKey.get(lineConfig.dataKey) ?? [],
              lineConfig.dataKey,
              hoveredTimeMs,
              isEnhancedMode
            );
            return nearest ? { lineConfig, ...nearest } : null;
          })
          .filter((item): item is { lineConfig: LineConfig } & NearestLinePoint => item !== null);

        if (hoveredLinePoints.length > 0) {
          const nearestHoverPoint = getNearestVisualLinePoint(
            hoveredLinePoints,
            mouseX,
            mouseY,
            (timestampMs) => xScale(new Date(timestampMs)),
            (value) => yScale(value)
          );
          if (!nearestHoverPoint) return;

          const tooltipTimeMs = nearestHoverPoint.pointTimeMs;
          const tooltipLinePoints = filterLinePointsAtTimestamp(hoveredLinePoints, tooltipTimeMs);
          const orderedTooltipLinePoints = [...tooltipLinePoints].sort((a, b) => {
            const activeDataKey = nearestHoverPoint.lineConfig.dataKey;
            if (a.lineConfig.dataKey === activeDataKey) return -1;
            if (b.lineConfig.dataKey === activeDataKey) return 1;
            return 0;
          });
          const x = xScale(new Date(tooltipTimeMs));
          const hoveredLinePointsByKey = new Map(
            tooltipLinePoints.map((item) => [item.lineConfig.dataKey, item])
          );

          focus.select(".hover-line").attr("x1", x).attr("x2", x);

          lines.forEach(lineConfig => {
            const hoveredLinePoint = hoveredLinePointsByKey.get(lineConfig.dataKey);
            const circle = focus.select(`.focus-circle-${lineConfig.dataKey}`);
            if (hoveredLinePoint) {
              circle
                .style("display", null)
                .attr("cx", xScale(new Date(hoveredLinePoint.pointTimeMs)))
                .attr("cy", yScale(hoveredLinePoint.value));
            } else {
              circle.style("display", "none");
            }
          });

          // Update tooltip
          const pointDate = new Date(tooltipTimeMs);
          const tooltipDateLabel = tooltipDateFormatter ? tooltipDateFormatter(pointDate) : isEnhancedMode
            ? daysDiff <= 3
              ? pointDate.toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  ...(useUtcDateLabels ? { timeZone: "UTC" } : {}),
                })
              : pointDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: daysDiff > 180 ? "numeric" : undefined,
                  ...(useUtcDateLabels ? { timeZone: "UTC" } : {}),
                })
            : nearestHoverPoint.dataPoint.date;
          let tooltipContent = `<div style="font-size: 12px; color: ${
            isDarkMode ? "#D1D5DB" : "#6B7280"
          }; margin-bottom: 4px;">${escapeHtml(tooltipDateLabel)}</div>`;

          orderedTooltipLinePoints.forEach(({ lineConfig, value }) => {
            const isActiveLine = lineConfig.dataKey === nearestHoverPoint.lineConfig.dataKey;
            const formattedValue = tooltipFormatter(value, lineConfig.dataKey);
            tooltipContent += `<div style="display: flex; align-items: center; margin-bottom: 2px;">
              <span style="display: inline-block; width: ${
                isActiveLine ? 10 : 8
              }px; height: ${isActiveLine ? 10 : 8}px; background-color: ${
                lineConfig.stroke
              }; border-radius: 50%; margin-right: 6px;"></span>
              <span style="font-size: 12px; font-weight: ${
                isActiveLine ? 700 : 400
              }; color: ${isDarkMode ? "#F9FAFB" : "#111827"};">${
              escapeHtml(lineConfig.label)
            }: <strong>${escapeHtml(formattedValue)}</strong></span>
            </div>`;
          });

          tooltip.html(tooltipContent);

          const tooltipNode = tooltip.node() as HTMLElement;
          const tooltipWidth = tooltipNode.offsetWidth;
          const tooltipHeight = tooltipNode.offsetHeight;
          const containerRect = containerRef.current!.getBoundingClientRect();

          // Get mouse position in viewport coordinates
          const mouseClientX = (event as MouseEvent).clientX;
          const mouseClientY = (event as MouseEvent).clientY;

          // Translate to container-relative coordinates and apply small offset
          let left = mouseClientX - containerRect.left + 12;
          let top = mouseClientY - containerRect.top - tooltipHeight / 2;

          // If tooltip would go off right edge of container, show it on the left side of cursor
          if (left + tooltipWidth > containerRect.width - 10) {
            left = mouseClientX - containerRect.left - tooltipWidth - 12;
          }

          // Prevent going off the left edge
          if (left < 10) {
            left = 10;
          }

          // If tooltip would go off top edge of container
          if (top < 10) {
            top = 10;
          }

          // If tooltip would go off bottom edge of container
          if (top + tooltipHeight > containerRect.height - 10) {
            top = containerRect.height - tooltipHeight - 10;
          }

          tooltip.style("left", `${left}px`).style("top", `${top}px`);
        }
      });

    // Add legend if enabled
    if (showLegend) {
      const legend = svg
        .append("g")
        .attr("class", "legend")
        .attr("transform", `translate(${margins.left + width / 2 - (lines.length * 80) / 2}, 10)`);

      lines.forEach((lineConfig, i) => {
        const legendItem = legend.append("g").attr("transform", `translate(${i * 120}, 0)`);

        legendItem
          .append("line")
          .attr("x1", 0)
          .attr("x2", 20)
          .attr("y1", 0)
          .attr("y2", 0)
          .attr("stroke", lineConfig.stroke)
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", lineConfig.strokeDasharray || "0");

        if (lineConfig.showDots !== false) {
          legendItem.append("circle").attr("cx", 10).attr("cy", 0).attr("r", 4).attr("fill", lineConfig.stroke);
        }

        legendItem
          .append("text")
          .attr("x", 26)
          .attr("y", 4)
          .attr("fill", textColor)
          .attr("font-size", "12px")
          .text(lineConfig.label);
      });
    }

    if (!isEnhancedMode) {
      const handleResize = () => {
        if (containerRef.current) {
          // Trigger re-render by forcing a state update in parent
          // The parent should re-render this hook with new dimensions
        }
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [config, containerWidth, isEnhancedMode]);

  return { svgRef, containerRef, tooltipRef };
};
